import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { usePersistingErrorStore } from '../../persisting-error/store';
import { useSnackbarStore } from '../../snackbar/store';
import { TermsState } from '../types';

export interface TermsRequirements {
    gatewayClient: GatewayApiClient;
}

function appOf(store: { $app: any }): TermsRequirements {
    return store.$app;
}

export const useTermsStore = defineStore('terms', {
    state: (): TermsState => ({
        signDetails: {},
        updateSign: {},
    }),
    actions: {
        startLoadingSignDetails(payload: { rpName: string }) {
            this.signDetails = {
                ...this.signDetails,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    signedBy: null,
                    signedAt: null,
                },
            };
        },
        finishLoadingSignDetails(payload: { rpName: string, signedBy?: string, signedAt?: number }) {
            const { rpName, signedBy, signedAt } = payload;
            this.signDetails = {
                ...this.signDetails,
                [rpName]: {
                    status: OperationStatus.success,
                    signedBy: signedBy || null,
                    signedAt: signedAt || null,
                },
            };
        },
        failLoadingSignDetails(payload: { rpName: string }) {
            this.signDetails = {
                ...this.signDetails,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    signedBy: null,
                    signedAt: null,
                },
            };
        },
        startUpdatingSign(payload: { rpName: string }) {
            this.updateSign = {
                ...this.updateSign,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishUpdatingSign(payload: { rpName: string }) {
            this.updateSign = {
                ...this.updateSign,
                [payload.rpName]: OperationStatus.success,
            };
        },
        failUpdatingSign(payload: { rpName: string }) {
            this.updateSign = {
                ...this.updateSign,
                [payload.rpName]: OperationStatus.error,
            };
        },
        async loadSignDetails(payload: { rpName: string }) {
            const { rpName } = payload;
            try {
                this.startLoadingSignDetails({ rpName });
                const sign = await appOf(this).gatewayClient.tprmUi().getTermsAndConditionsSign(rpName);
                this.finishLoadingSignDetails({
                    signedAt: sign?.tcSigned,
                    signedBy: sign?.tcSigner,
                    rpName,
                });
            } catch (error) {
                this.failLoadingSignDetails({ rpName });
                await usePersistingErrorStore().showError(error);
            }
        },
        async signTerms(payload: { rpName: string }) {
            const { rpName } = payload;
            try {
                this.startUpdatingSign({ rpName });
                await appOf(this).gatewayClient.tprmUi().signTermsAndConditions(rpName);
                this.finishUpdatingSign({ rpName });
                this.finishLoadingSignDetails({
                    signedAt: Math.floor(Date.now() / 1000),
                    signedBy: 'you',
                    rpName,
                });
                useSnackbarStore().displaySnackbar({
                    success: true,
                    message: 'Successfully accepted the terms and conditions.',
                });
            } catch (error) {
                this.failUpdatingSign({ rpName });
                useSnackbarStore().displaySnackbar({
                    success: false,
                    permanent: true,
                    message: 'Sorry, something went wrong. Please try again later.',
                });
            }
        },
    },
});
